import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { idle, TESTING_IDLE_OPERATOR_MOCK } from '@freelancer/operators';
import { PreloadRoute } from '@freelancer/preload-route';
import { Pwa } from '@freelancer/pwa';
import { Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import { isExperimentalSwControlling } from './experimental-service-worker';

/**
 * Warms commonly navigated to routes once the app has become idle.
 */
@Injectable({
  providedIn: 'root',
})
export class PreloadEssentialRoutes implements OnDestroy {
  private auth = inject(Auth);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private preloadRoute = inject(PreloadRoute);
  private pwa = inject(Pwa);
  private router = inject(Router);
  private idle = inject(TESTING_IDLE_OPERATOR_MOCK, { optional: true }) ?? idle;

  private subscriptions = new Subscription();

  start(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.subscriptions.add(
      this.router.events
        .pipe(
          // The router preloader registers no route with PreloadRoute until the
          // first navigation has ended, and preloading before that silently
          // does nothing.
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd,
          ),
          take(1),
          // A worker does not control the page that registered it, so on a
          // first visit wait for the experimental one to claim the page rather
          // than giving up on it.
          switchMap(() => this.pwa.serviceWorkerControllerScriptUrl()),
          filter(scriptUrl => isExperimentalSwControlling(scriptUrl)),
          take(1),
          // Warm the set this session needs, then the other set if the user
          // logs in or out without reloading the app.
          switchMap(() => this.auth.isLoggedIn()),
          distinctUntilChanged(),
          switchMap(isLoggedIn =>
            this.idle(this.ngZone).pipe(map(() => isLoggedIn)),
          ),
        )
        .subscribe(isLoggedIn => {
          this.preloadRoute.preloadEssentialRoutes(
            isLoggedIn ? 'logged-in' : 'logged-out',
          );
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
